export type ReasoningTagMode = "strict" | "preserve";
export type ReasoningTagTrim = "none" | "start" | "both";

const QUICK_TAG_RE = /<\s*\/?\s*(?:think(?:ing)?|thought|antthinking|final)\b/i;
const FINAL_TAG_RE = /<\s*\/?\s*final\b[^<>]*>/gi;
const THINKING_TAG_RE = /<\s*(\/?)\s*(?:think(?:ing)?|thought|antthinking)\b[^<>]*>/gi;

interface CodeRegion {
  start: number;
  end: number;
}

function findCodeRegions(text: string): CodeRegion[] {
  const regions: CodeRegion[] = [];

  const fencedRe = /(^|\n)(```|~~~)[^\n]*\n[\s\S]*?(?:\n\2(?:\n|$)|$)/g;
  for (const match of text.matchAll(fencedRe)) {
    const start = (match.index ?? 0) + match[1].length;
    regions.push({ start, end: start + match[0].length - match[1].length });
  }

  const inlineRe = /`+[^`]+`+/g;
  for (const match of text.matchAll(inlineRe)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    const insideFenced = regions.some((r) => start >= r.start && end <= r.end);
    if (!insideFenced) {
      regions.push({ start, end });
    }
  }

  regions.sort((a, b) => a.start - b.start);
  return regions;
}

function isInsideCode(pos: number, regions: CodeRegion[]): boolean {
  return regions.some((r) => pos >= r.start && pos < r.end);
}

function applyTrim(value: string, mode: ReasoningTagTrim): string {
  if (mode === "none") {
    return value;
  }
  if (mode === "start") {
    return value.trimStart();
  }
  return value.trim();
}

/**
 * Returns only the text that appeared inside `<final>...</final>` blocks,
 * stripping the tags themselves. If no `<final>` tags are present, returns
 * an empty string. Preserves code regions (fenced blocks and inline spans)
 * and handles unclosed final tags by including the trailing content.
 *
 * Used as a safety net for reasoning-tag providers (e.g. MiniMax) where the
 * system prompt instructs the model to wrap its user-facing reply in
 * `<final>...</final>`. If the model emits "thinking out loud" without those
 * markers, every word is treated as reasoning and dropped — preventing the
 * raw internal monologue from leaking to the user (e.g. Telegram).
 */
export function extractFinalTagContent(text: string): string {
  if (!text) {
    return text;
  }
  if (!/<final\b/i.test(text)) {
    return "";
  }
  const codeRegions = findCodeRegions(text);
  const matches = Array.from(text.matchAll(FINAL_TAG_RE));
  let result = "";
  let lastIndex = 0;
  let inFinal = false;
  for (const match of matches) {
    const idx = match.index ?? 0;
    if (isInsideCode(idx, codeRegions)) {
      continue;
    }
    // `FINAL_TAG_RE` does not capture the slash, so detect close from the
    // matched text directly. Opening tags look like `<final>` or
    // `<final ...>`; closing tags look like `</final>` or `</final ...>`.
    const isClose = match[0].startsWith("</");
    if (!inFinal && !isClose) {
      // Opening <final> tag — start capturing after it.
      lastIndex = idx + match[0].length;
      inFinal = true;
    } else if (inFinal && isClose) {
      // Closing </final> — flush captured content and stop.
      result += text.slice(lastIndex, idx);
      inFinal = false;
      lastIndex = idx + match[0].length;
    }
  }
  // Unclosed final tag — include everything after the opener.
  if (inFinal) {
    result += text.slice(lastIndex);
  }
  return result;
}

export function stripReasoningTagsFromText(
  text: string,
  options?: {
    mode?: ReasoningTagMode;
    trim?: ReasoningTagTrim;
    /**
     * When true, treat `<final>...</final>` as a hard gate: return only the
     * text inside those tags and drop everything else. Reasoning-tag providers
     * are expected to wrap user-facing replies in `<final>`; if they don't,
     * the whole message is treated as reasoning and discarded. This prevents
     * models from leaking "thinking out loud" prose to the user.
     */
    enforceFinalTag?: boolean;
  },
): string {
  if (!text) {
    return text;
  }
  if (!QUICK_TAG_RE.test(text)) {
    return text;
  }

  const mode = options?.mode ?? "strict";
  const trimMode = options?.trim ?? "both";
  const enforceFinalTag = options?.enforceFinalTag ?? false;

  // Hard gate: if enforceFinalTag is on and any <final> tag is present, only
  // return text from inside <final>...</final> blocks. Never fall through.
  if (enforceFinalTag && /<final\b/i.test(text)) {
    return applyTrim(extractFinalTagContent(text), trimMode);
  }

  let cleaned = text;
  if (FINAL_TAG_RE.test(cleaned)) {
    FINAL_TAG_RE.lastIndex = 0;
    const finalMatches: Array<{ start: number; length: number; inCode: boolean }> = [];
    const preCodeRegions = findCodeRegions(cleaned);
    for (const match of cleaned.matchAll(FINAL_TAG_RE)) {
      const start = match.index ?? 0;
      finalMatches.push({
        start,
        length: match[0].length,
        inCode: isInsideCode(start, preCodeRegions),
      });
    }

    for (let i = finalMatches.length - 1; i >= 0; i--) {
      const m = finalMatches[i];
      if (!m.inCode) {
        cleaned = cleaned.slice(0, m.start) + cleaned.slice(m.start + m.length);
      }
    }
  } else {
    FINAL_TAG_RE.lastIndex = 0;
  }

  // Hard gate: enforceFinalTag is on but no <final> tag was found. Treat the
  // entire text as reasoning and drop it — the model failed to wrap its reply.
  if (enforceFinalTag) {
    return "";
  }

  const codeRegions = findCodeRegions(cleaned);

  THINKING_TAG_RE.lastIndex = 0;
  let result = "";
  let lastIndex = 0;
  let inThinking = false;

  for (const match of cleaned.matchAll(THINKING_TAG_RE)) {
    const idx = match.index ?? 0;
    const isClose = match[1] === "/";

    if (isInsideCode(idx, codeRegions)) {
      continue;
    }

    if (!inThinking) {
      result += cleaned.slice(lastIndex, idx);
      if (!isClose) {
        inThinking = true;
      }
    } else if (isClose) {
      inThinking = false;
    }

    lastIndex = idx + match[0].length;
  }

  if (!inThinking || mode === "preserve") {
    result += cleaned.slice(lastIndex);
  }

  return applyTrim(result, trimMode);
}
